/** @jsxImportSource @emotion/react */
import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  TypeOuter,
  TypeContainer,
  DragHandle,
  TypeTextarea,
  BottomActions,
  IconBtn,
  FileStrip,
  FileChip,
  FileChipLabel,
  FileChipRemove,
} from './type.styled';
import { useType } from './type.hooks';
import { FILE_ACCEPT, MAX_TOTAL_FILE_SIZE, validateFiles, formatFileSize } from '../../../../constants/upload-limits';

type TypeProps = {
  onClose: () => void;
  onMicClick: () => void;
  onSubmitMessage: (text: string, files?: File[]) => Promise<void> | void;
  disabled?: boolean;
  uploadProgress?: number | null | undefined;
  onError?: ((msg: string) => void) | undefined;
  isExiting?: boolean;
  onExitEnd?: () => void;
};

export const Type: React.FC<TypeProps> = ({ onClose, onMicClick, onSubmitMessage, disabled = false, uploadProgress, onError, isExiting = false, onExitEnd }) => {
  const {
    value,
    setValue,
    maxChars,
    recordOrSend,
    isSendMode,
  } = useType({ onClose, onMicClick });

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Touch-based drag to dismiss (same pattern as RecordDock)
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [isSnappingBack, setIsSnappingBack] = useState(false);
  const [isSlidingOut, setIsSlidingOut] = useState(false);
  const dragStartY = useRef<number | null>(null);
  const isDragging = useRef(false);
  const DRAG_THRESHOLD = 80;

  const handleDragStart = (clientY: number) => {
    dragStartY.current = clientY;
    isDragging.current = false;
  };
  const handleDragMove = (clientY: number) => {
    if (dragStartY.current === null) return;
    const delta = clientY - dragStartY.current;
    if (delta > 5) isDragging.current = true;
    setDragOffsetY(Math.max(0, delta));
  };
  const handleDragEnd = (clientY?: number) => {
    if (dragStartY.current === null) return;
    const delta = clientY != null ? clientY - dragStartY.current : 0;
    const offset = Math.max(0, delta);
    dragStartY.current = null;
    if (offset >= DRAG_THRESHOLD) {
      setIsSlidingOut(true);
      setDragOffsetY(999);
      onClose();
    } else if (offset > 0) {
      setIsSnappingBack(true);
      setDragOffsetY(0);
      setTimeout(() => setIsSnappingBack(false), 200);
    }
  };

  // ✅ NEW: local attachments (since Type has no file state yet)
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ✅ auto-grow up to 200px, then scroll
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    el.style.height = 'auto';

    const next = Math.min(el.scrollHeight, 200);
    el.style.height = `${next}px`;

    el.style.overflowY = el.scrollHeight > 200 ? 'auto' : 'hidden';
  }, [value]);

  const attach = () => {
    if (disabled || isSending) return;
    fileInputRef.current?.click();
  };

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected) return;

    const { accepted, rejected } = validateFiles(Array.from(selected));

    // Additional checks: duplicates and cumulative size
    const currentSize = files.reduce((sum, f) => sum + f.size, 0);
    const finalAccepted: File[] = [];
    let runningSize = currentSize;

    for (const file of accepted) {
      const isDuplicate =
        files.some((f) => f.name === file.name && f.size === file.size) ||
        finalAccepted.some((f) => f.name === file.name && f.size === file.size);
      if (isDuplicate) {
        rejected.push(`"${file.name}" is already attached`);
        continue;
      }
      if (runningSize + file.size > MAX_TOTAL_FILE_SIZE) {
        rejected.push(`"${file.name}" exceeds ${formatFileSize(MAX_TOTAL_FILE_SIZE)} total limit`);
        continue;
      }
      finalAccepted.push(file);
      runningSize += file.size;
    }

    if (rejected.length > 0) {
      onError?.(rejected.join('. '));
    }
    if (finalAccepted.length > 0) {
      setFiles((prev) => [...prev, ...finalAccepted]);
    }

    // reset so selecting same file again triggers onChange
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const [isSending, setIsSending] = React.useState(false);

  const handleSend = async () => {
    const text = (value || "").trim();

    // Keep your existing behavior: don’t send empty unless files exist
    if (!text && files.length === 0) return;
    if (disabled || isSending) return;

    setIsSending(true);
    const toSendFiles = files.length ? files : undefined;

    try {
      await onSubmitMessage(text, toSendFiles);
      import('../../../../platform/haptics').then(h => h.hapticLight());
      // Only clear on success - preserve input on failure
      // Note: closing is handled by chat.tsx via closeTypeImmediate()
      setValue("");
      setFiles([]);
    } catch {
      // Error toast handled by caller (chat.tsx) - keep input intact
    } finally {
      setIsSending(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (disabled || isSending) return;

    if (isSendMode || files.length > 0) {
      await handleSend();
      return;
    }

    // otherwise keep current mic behavior
    recordOrSend();
  };

  return (
    <div
      style={{
        // position: 'fixed',
        // left: 0,
        // right: 0,
        // bottom: keyboardOpen ? 10 : `calc(env(safe-area-inset-bottom, 0px) - 12px)`,
        // paddingBottom: keyboardOpen ? 0 : `calc(env(safe-area-inset-bottom, 0px) + 16px)`,
        // paddingBottom: keyboardOpen ? 0 : `calc(env(safe-area-inset-bottom, 0px) + 16px)`,
        width: '100%',
        zIndex: 60,
        boxSizing: 'border-box',
      }}
    >
    <TypeOuter
      initial={{ y: '100%' }}
      animate={isExiting || isSlidingOut ? { y: '100%', opacity: 0 } : { y: dragOffsetY, opacity: dragOffsetY > 0 ? Math.max(0.3, 1 - dragOffsetY / 200) : 1 }}
      transition={isDragging.current ? { type: 'tween', duration: 0 } : isSnappingBack ? { type: 'spring', stiffness: 500, damping: 40 } : { type: 'spring', stiffness: 420, damping: 34 }}
      onAnimationComplete={() => { if (isExiting) onExitEnd?.(); }}
    >
        <TypeContainer>
      <div
        onTouchStart={(e) => handleDragStart(e.touches[0].clientY)}
        onTouchMove={(e) => handleDragMove(e.touches[0].clientY)}
        onTouchEnd={(e) => handleDragEnd(e.changedTouches[0]?.clientY)}
        onMouseDown={(e) => handleDragStart(e.clientY)}
        onMouseMove={(e) => { if (e.buttons === 1) handleDragMove(e.clientY); }}
        onMouseUp={(e) => handleDragEnd(e.clientY)}
        onMouseLeave={(e) => { if (dragStartY.current !== null) handleDragEnd(e.clientY); }}
        style={{ display: 'flex', justifyContent: 'center', padding: '6px 0', cursor: 'grab' }}
      >
        <DragHandle />
      </div>

          {files.length > 0 && (
            <FileStrip>
              {files.map((file, index) => (
                <FileChip key={index}>
                  <img src="/file.svg" alt="file" width={16} height={16} style={{ flexShrink: 0 }} />
                  <FileChipLabel>{file.name}</FileChipLabel>
                  <FileChipRemove
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => removeFile(index)}
                  >
                    ×
                  </FileChipRemove>
                </FileChip>
              ))}
            </FileStrip>
          )}

          <TypeTextarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            maxLength={maxChars}
            placeholder="Start typing..."
            aria-label="Type message"
          />

          {uploadProgress != null && uploadProgress > 0 && (
            <div style={{ margin: '0 0 4px' }}>
              <div
                style={{
                  height: 3,
                  background: '#5E84ED',
                  borderRadius: 9999,
                  width: `${uploadProgress}%`,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          )}

          <BottomActions>
            <IconBtn
              type="button"
              onPointerDown={(e) => e.preventDefault()}
              onClick={attach}
              aria-label="Attach"
            >
              <img src="/attach-dark-bg.svg" alt="Attach" />
            </IconBtn>

            <IconBtn
              type="button"
              onClick={handlePrimaryAction}
              aria-label={isSendMode || files.length > 0 ? 'Send' : 'Microphone'}
              style={disabled || isSending ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
            >
              <img
                src={isSendMode || files.length > 0 ? '/send-with-bg.svg' : '/microphone-blue-bg.svg'}
                alt={isSendMode || files.length > 0 ? 'Send' : 'Microphone'}
              />
            </IconBtn>
          </BottomActions>

          {/* ✅ hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={onFileSelect}
            accept={FILE_ACCEPT}
          />
        </TypeContainer>
    </TypeOuter>
    </div>
  );
};
